import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQProps {
  title?: string;
  subtitle?: string;
  faqs: FAQItem[];
  ctaText?: string;
}

const FAQ: React.FC<FAQProps> = ({ 
  title = "Frequently Asked Questions",
  subtitle = "Find answers to common questions about our services, features, and how to get started.",
  faqs,
  ctaText = "Still have questions? We're here to help!"
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [faqRef, faqVisible] = useScrollAnimation();

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section ref={faqRef} className="py-20 px-4 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className={`text-center mb-12 transition-all duration-700 ${
          faqVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-4 tracking-tight">
            {title}
          </h2>
          <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`bg-secondary-100 rounded-lg border-2 border-secondary-200 transition-all duration-500 ${
                faqVisible 
                  ? `animate-fade-in-up opacity-100` 
                  : 'opacity-0'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-secondary-200 transition-colors rounded-lg"
              >
                <span className="text-lg font-semibold text-secondary-900 pr-4">
                  {faq.question}
                </span>
                <ChevronDown
                  className={`flex-shrink-0 text-primary-600 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                  size={24}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-5 pt-0">
                  <p className="text-secondary-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`text-center mt-12 transition-all duration-700 ${
          faqVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`} style={{ transitionDelay: `${faqs.length * 100}ms` }}>
          <p className="text-secondary-600 text-lg">
            {ctaText}
          </p>
        </div>
      </div>
    </section>
  );
};

export default FAQ;

